<template>
  <div class="login-page">
    
    <!-- 左侧宣传区 -->
    <div class="login-left">
      <div class="left-content">
        <div class="group-1">
          <div class="div">{{ siteTitle }}</div>
          <div class="subtitle">
            {{ siteSubtitle }}
          </div>
          <div class="frame-3">
            <el-button type="primary" round class="read-more">{{ $t('platform.readMore') }}</el-button>
          </div>
        </div>
        <div class="group-2">
          <div class="ellipse-1"></div>
          <div class="ellipse-2"></div>
        </div>
      </div>
    </div>
    <!-- 右侧登录区 -->
    <div class="login-right">
      <div class="login-form">
        <div class="language-switcher-container">
          <LanguageSwitcher />
        </div>
        <h2 style="color: #ffffff;">找回密码</h2>
        <p class="welcome">通过手机号重置您的密码</p>
        
        <!-- 找回密码表单 -->
        <el-form :model="forgetForm" :rules="forgetRules" ref="forgetFormRef" class="form">
          <el-form-item prop="phone">
            <el-input clearable
              v-model="forgetForm.phone"
              :placeholder="$t('common.phoneNumber')"
              prefix-icon="el-icon-phone"
              :style="{ borderRadius: '22px', height: '44px' }"
            />
          </el-form-item>
          <el-form-item prop="verificationCode">
            <div class="verification-code-container">
              <el-input clearable
                v-model="forgetForm.verificationCode"
                :placeholder="$t('common.verificationCode')"
                prefix-icon="el-icon-message"
                :style="{ borderRadius: '22px', height: '44px', flex: 1 }"
              />
              <el-button 
                type="primary" 
                :disabled="!canSendCode || countdown > 0"
                @click="sendVerificationCode"
                :loading="sendingCode"
                class="send-code-btn"
                round
              >
                {{ countdown > 0 ? `${countdown}${$t('common.countdown')}` : $t('common.sendCode') }}
              </el-button>
            </div>
          </el-form-item>
          <el-form-item prop="password">
            <el-input clearable
              v-model="forgetForm.password"
              type="password"
              :placeholder="$t('common.password')"
              prefix-icon="el-icon-lock"
              show-password
              :style="{ borderRadius: '22px', height: '44px' }"
            />
          </el-form-item>
          <el-form-item prop="confirmPassword">
            <el-input clearable
              v-model="forgetForm.confirmPassword"
              type="password"
              :placeholder="$t('common.confirmPassword')"
              prefix-icon="el-icon-lock"
              show-password
              :style="{ borderRadius: '22px', height: '44px' }"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" class="login-btn" @click="onForgetPassword" :loading="loading" round block color="#079196">重置密码</el-button>
          </el-form-item>
          <el-form-item>
            <el-button link class="back-login-btn" @click="goToLogin" style="color: #079196;">返回登录</el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onUnmounted, onMounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import LanguageSwitcher from '@/apps/portal/components/LanguageSwitcher.vue'
import { forgetPassword, sendVerificationCode as sendCode } from '@/api/portal/user'
import { getAgentInfo, initAgentInfo } from '@/stores/portal/agentStore'
import { getAgentFromStorage } from '@/apps/portal/utils/agentUtils'

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()

// 获取响应式的代理信息
const agentInfo = getAgentInfo()

// 使用 computed 响应式获取标题和副标题
const siteTitle = computed(() => {
  return agentInfo.siteTitle || '大象魔法AIGC'
})

const siteSubtitle = computed(() => {
  return agentInfo.siteSubtitle || '魔法在手，驰骋全球'
})

// 忘记密码表单
const forgetForm = reactive({
  phone: '',
  verificationCode: '',
  password: '',
  confirmPassword: '',
})

// 验证码相关状态
const countdown = ref(0)
const sendingCode = ref(false)
const canSendCode = computed(() => {
  const phoneRegex = /^1[3-9]\d{9}$/
  return phoneRegex.test(forgetForm.phone)
})

let countdownTimer = null

// 密码验证函数
const validatePass = (rule, value, callback) => {
  if (value === '') {
    callback(new Error(t('validation.passwordRequired')))
  } else if (value.length < 6) {
    callback(new Error(t('validation.passwordLength')))
  } else {
    callback()
  }
}

const validatePass2 = (rule, value, callback) => {
  if (value === '') {
    callback(new Error(t('validation.confirmPasswordRequired')))
  } else if (value !== forgetForm.password) {
    callback(new Error(t('validation.passwordMismatch')))
  } else {
    callback()
  }
}

// 手机号验证函数
const validatePhone = (rule, value, callback) => {
  const phoneRegex = /^1[3-9]\d{9}$/
  if (value === '') {
    callback(new Error(t('validation.phoneRequired')))
  } else if (!phoneRegex.test(value)) {
    callback(new Error(t('validation.phoneInvalid')))
  } else {
    callback()
  }
}

// 忘记密码验证规则
const forgetRules = {
  phone: [
    { required: true, validator: validatePhone, trigger: 'blur' }
  ],
  verificationCode: [
    { required: true, message: t('validation.verificationCodeRequired'), trigger: 'blur' }
  ],
  password: [
    { required: true, validator: validatePass, trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, validator: validatePass2, trigger: 'blur' }
  ]
}

const forgetFormRef = ref()
const loading = ref(false)

// 发送验证码
const sendVerificationCode = async () => {
  if (!canSendCode.value) {
    ElMessage.error(t('validation.phoneInvalid'))
    return
  }

  sendingCode.value = true
  try {
    const response = await sendCode(forgetForm.phone)
    if (response.code === 0) {
      ElMessage.success('验证码发送成功')
      startCountdown()
    } else {
      ElMessage.error(response.msg || '验证码发送失败')
    }
  } catch (error) {
    console.error('Send verification code error:', error)
    ElMessage.error('验证码发送失败')
  } finally {
    sendingCode.value = false
  }
}

// 忘记密码
const onForgetPassword = async () => {
  if (!forgetFormRef.value) return
  
  await forgetFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        const response = await forgetPassword({
          phone: forgetForm.phone,
          verificationCode: forgetForm.verificationCode,
          password: forgetForm.password,
          confirmPassword: forgetForm.confirmPassword
        })
        
        if (response.code === 0) {
          ElMessage.success('密码重置成功')
          // 重置成功后跳转到登录页面
          router.push('/login')
        } else {
          ElMessage.error(response.msg || '密码重置失败')
        }
      } catch (error) {
        console.error('Forget password error:', error)
        ElMessage.error('密码重置失败')
      } finally {
        loading.value = false
      }
    }
  })
}

// 开始倒计时
const startCountdown = () => {
  countdown.value = 60
  countdownTimer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(countdownTimer)
    }
  }, 1000)
}

// 跳转到登录页面
const goToLogin = () => {
  const agentId = route.query.agent || getAgentFromStorage()
  if (agentId) {
    router.push({ path: '/login', query: { agent: agentId } })
  } else {
    router.push('/login')
  }
}

// 页面挂载时确保代理信息已加载
onMounted(async () => {
  await initAgentInfo()
  await nextTick()
})

// 组件卸载时清理定时器
onUnmounted(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
})
</script>

<style scoped>
html, body, #app {
  height: 100%;
  margin: 0;
  padding: 0;
}

.login-page {
  display: flex;
  min-height: 100vh;
  background: #000;
  position: relative;
  z-index: 1;
}

.login-page::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  z-index: -1;
}

.bg-lines {
  position: absolute;
  left: 0; top: 0; width: 100%; height: 100%; pointer-events: none; z-index: 1;
}

.group-1 {
  position: relative;
  z-index: 3;
}
.div {
  color: var(--white, #ffffff);
  text-align: left;
  font-family: "Poppins-Bold", sans-serif;
  font-size: 40px;
  font-weight: 700;
  left: 0px;
  top: 0px;
}
.subtitle {
  font-size: 1.2rem;
  margin-bottom: 30px;
}
.read-more {
  margin-bottom: 40px;
}
.login-left, .login-right {
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

.login-left {
  flex: 1.2;
  background: linear-gradient(
      180deg,
      rgb(30, 30, 30) 0%,
      rgb(16, 16, 16) 60%,
      rgb(0, 0, 0) 100%
    );
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.left-content {
  margin-top: 30px;
  width: 80%;
  max-width: 500px;
  position: relative;
  z-index: 2;
}
.left-content h1 {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 10px;
}


.more-btn {
  grid-column: 6 / 7;
  margin-left: 0;
  justify-self: end;
}
.login-right {
  flex: 1;
  background: #262626;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  border-left: 1px solid #303030;
}
.login-form {
  width: 350px;
  padding: 40px 30px;
  border-radius: 18px;
  background: #262626;
  text-align: center;
}
.login-form h2 {
  font-size: 1.7rem;
  font-weight: bold;
  margin-bottom: 8px;
  color: #222;
}
.welcome {
  color: #888;
  margin-bottom: 30px;
}
.form {
  margin-bottom: 10px;
}
.login-btn {
  width: 100%;
  height: 44px;
  font-size: 1.1rem;
  border-radius: 22px;
  color: #ffffff;
}
.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}
.forgot {
  color: #888;
  text-decoration: none;
  font-size: 0.95rem;
}
.forgot:hover {
  color: #00c1cd;
}
.register-btn {
  color: #00c1cd;
  text-decoration: none;
  font-size: 0.95rem;
}
.register-btn:hover {
  color: #00c1cd;
}
@media (max-width: 900px) {
  .login-left {
    display: none;
  }
  .login-right {
    flex: 1 1 100%;
    width: 100vw;
    min-width: 0;
  }
  .login-form {
    width: 95vw;
    min-width: 0;
    padding: 24px 8px 16px 8px;
    border-radius: 8px;
    box-shadow: none;
  }
  .login-form h2 {
    font-size: 1.3rem;
  }
  .welcome {
    font-size: 1rem;
  }
  .login-btn {
    font-size: 1rem;
    height: 40px;
  }
}

/* 忘记密码相关样式 */
.form-header {
  position: relative;
  margin-bottom: 20px;
}

.forget-password-btn {
  width: 100%;
  text-align: center;
  margin-top: 10px;
}
@media (max-width: 480px) {
  .login-form {
    width: 100vw;
    padding: 12px 2vw 8px 2vw;
  }
  .login-form h2 {
    font-size: 1.1rem;
  }
  .welcome {
    font-size: 0.95rem;
  }
}
.bg-lines {
  position: absolute;
  left: 0; top: 0; width: 100%; height: 100%; pointer-events: none; z-index: 1;
}
:deep(.el-input__wrapper) {
  border-radius: 22px !important;
  height: 44px !important;
  align-items: center;
}
:deep(.el-input__inner) {
  height: 44px !important;
  line-height: 44px !important;
}

.group-2 {
  position: fixed;
  left: -260px;
  bottom: -380px;
  z-index: 1;
  width: 600px;
  height: 600px;
  pointer-events: none;
}
.ellipse-1, .ellipse-2 {
  border-radius: 50%;
  border-style: solid;
  border-width: 1px;
  width: 557px;
  height: 557px;
  position: absolute;
}
.ellipse-1 {
  left: 40px;
  bottom: 40px;
  top: auto;
  border-color: #079196;
}
.ellipse-2 {
  left: 140px;
  bottom: 0;
  top: auto;
  border-color: #ad063a;
}

.language-switcher-container {
  position: absolute;
  top: 20px;
  right: 20px;
}

.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.register-btn {
  font-size: 0.95rem;
}

.legal-links {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  font-size: 0.85rem;
}

.legal-link {
  color: #888;
  text-decoration: none;
  transition: color 0.3s ease;
}

.legal-link:hover {
  color: #00c1cd;
}

.separator {
  color: #888;
  margin: 0 8px;
}

.verification-code-container {
  display: flex;
  gap: 10px;
  align-items: center;
}

.send-code-btn {
  height: 44px;
  min-width: 120px;
  border-radius: 22px;
  font-size: 0.9rem;
  white-space: nowrap;
}

.back-login-btn {
  width: 100%;
  text-align: center;
  margin-top: 10px;
  color: #00c1cd;
  text-decoration: none;
  font-size: 0.95rem;
}

.back-login-btn:hover {
  color: #00c1cd;
}

@media (max-width: 480px) {
  .verification-code-container {
    flex-direction: column;
    gap: 8px;
  }
  
  .send-code-btn {
    width: 100%;
    min-width: auto;
  }
}
</style>
